import React, { useRef, useState } from "react";
import BusinessIcon from "@mui/icons-material/Business";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import { toast } from "react-toastify";
import baseUrl from "../../../api/baseUrl";
import {
  BoolBadge,
  DetailRow,
  DetailsBlock,
  formatBytes,
  formatDate,
  MetricTile,
  PanelCard,
  SectionHeader,
} from "../components/accountUi";
import ImageCropModal from "./ImageCropModal";

const LOGO_SIZE = { width: 200, height: 60 };
const LOGO_SMALL_SIZE = { width: 30, height: 30 };

export type ClinicData = {
  clinicName?: string;
  clinicId?: string;
  address?: string;
  registerNo?: string;
  ownerName?: string;
  tpNo?: string;
  title?: string;
  regNoMethod?: string;
  regNoFormat?: string;
  reqNoStart?: string;
  startTime?: string;
  endTime?: string;
  timeDuration?: string;
  employeeCount?: number;
  advancedChart?: boolean;
  appointmentApp?: boolean;
  featureList?: {
    labs?: boolean;
    roomAllocation?: boolean;
    printTemplate_2?: boolean;
    doctorTransfer?: boolean;
  };
  appDocLimit?: number;
  logo?: boolean;
  logoimage?: string;
  logoSmallimage?: string;
  printImages?: string;
  headerImages?: string;
  footerImages?: string;
  queueVideos?: {
    originalName?: string;
    mime?: string;
    size?: number;
    uploadedAt?: string;
  }[];
  createdAt?: string;
  updatedAt?: string;
};

function SubsectionTitle({ children }: { children: string }) {
  return (
    <h4 className="mt-8 mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-400 first:mt-0 dark:text-slate-500">
      <span className="h-px flex-1 bg-gradient-to-r from-slate-200 to-transparent dark:from-slate-600" />
      <span className="shrink-0">{children}</span>
      <span className="h-px flex-1 bg-gradient-to-l from-slate-200 to-transparent dark:from-slate-600" />
    </h4>
  );
}

function resolveMediaSrc(value?: string | null) {
  const key = typeof value === "string" ? value.trim() : "";
  if (!key) return null;
  if (/^https?:\/\//i.test(key)) return key;
  if (key.includes("/uploads/images")) {
    return `${process.env.REACT_APP_API ?? ""}${key}`;
  }
  return null;
}

type CropTarget = "logo" | "logoSmall" | "print";

type ClinicTabPanelProps = {
  clinic: ClinicData | null;
  clinicNameFallback?: string;
  addressFallback?: string;
  phoneFallback?: string;
  logoUrl: string | null;
  onClinicRefresh?: () => Promise<unknown>;
};

export default function ClinicTabPanel({
  clinic,
  clinicNameFallback,
  addressFallback,
  phoneFallback,
  logoUrl,
  onClinicRefresh,
}: ClinicTabPanelProps) {
  const queueCount = clinic?.queueVideos?.length ?? 0;
  const displayName = clinic?.clinicName || clinicNameFallback || "—";
  const logoInputRef = useRef<HTMLInputElement>(null);
  const logoSmallInputRef = useRef<HTMLInputElement>(null);
  const printInputRef = useRef<HTMLInputElement>(null);

  const [cropTarget, setCropTarget] = useState<CropTarget | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [pendingLogo, setPendingLogo] = useState<string | null>(null);
  const [pendingLogoSmall, setPendingLogoSmall] = useState<string | null>(null);
  const [pendingPrint, setPendingPrint] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const logoSrc = pendingLogo || resolveMediaSrc(clinic?.logoimage) || logoUrl;
  const logoSmallSrc = pendingLogoSmall || resolveMediaSrc(clinic?.logoSmallimage);
  const printSrc = pendingPrint || resolveMediaSrc(clinic?.printImages);

  const otherBranding = [
    { label: "Header image", src: resolveMediaSrc(clinic?.headerImages) },
    { label: "Footer image", src: resolveMediaSrc(clinic?.footerImages) },
  ].filter((item): item is { label: string; src: string } => Boolean(item.src));

  const openCrop = (file: File, target: CropTarget) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.readyState === 2) {
        setCropSrc(reader.result as string);
        setCropTarget(target);
      }
    };
    reader.readAsDataURL(file);
  };

  const closeCrop = () => {
    setCropSrc(null);
    setCropTarget(null);
  };

  const applyCrop = (dataUrl: string) => {
    if (cropTarget === "logo") setPendingLogo(dataUrl);
    if (cropTarget === "logoSmall") setPendingLogoSmall(dataUrl);
    if (cropTarget === "print") setPendingPrint(dataUrl);
    closeCrop();
  };

  const discardPending = () => {
    setPendingLogo(null);
    setPendingLogoSmall(null);
    setPendingPrint(null);
  };

  const saveBranding = async () => {
    if (!pendingLogo && !pendingLogoSmall && !pendingPrint) return;
    setSaving(true);
    try {
      const payload: { image?: string; logoSmallImage?: string; printImage?: string } = {};
      if (pendingLogo) payload.image = pendingLogo;
      if (pendingLogoSmall) payload.logoSmallImage = pendingLogoSmall;
      if (pendingPrint) payload.printImage = pendingPrint;

      const res = await baseUrl.patch("/clinic/updateClinicLogos", payload);
      if (!res?.data?.success) {
        throw new Error(res?.data?.message || "Failed to update branding images");
      }
      toast.success("Branding images updated");
      discardPending();
      await onClinicRefresh?.();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || err?.message || "Could not save branding images"
      );
    } finally {
      setSaving(false);
    }
  };

  const hasPending = Boolean(pendingLogo || pendingLogoSmall || pendingPrint);

  const cropTitle =
    cropTarget === "logo"
      ? "Crop logo"
      : cropTarget === "logoSmall"
        ? "Crop logo small"
        : "Crop print image";

  const renderEditableCard = ({
    label,
    sizeLabel,
    src,
    pending,
    inputRef,
    target,
    emptyLabel,
    imgClassName,
  }: {
    label: string;
    sizeLabel?: string;
    src: string | null;
    pending: boolean;
    inputRef: React.MutableRefObject<HTMLInputElement | null>;
    target: CropTarget;
    emptyLabel: string;
    imgClassName: string;
  }) => (
    <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-slate-50 dark:border-slate-600/80 dark:bg-slate-900/40">
      <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2 dark:border-slate-700">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
          {label}
          {sizeLabel ? ` · ${sizeLabel}` : null}
        </p>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold text-primary hover:bg-primary/10"
        >
          {src ? (
            <>
              <EditOutlinedIcon sx={{ fontSize: 14 }} />
              Edit
            </>
          ) : (
            <>
              <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 14 }} />
              Add
            </>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) openCrop(file, target);
            e.target.value = "";
          }}
        />
      </div>
      <div className="flex min-h-[7.5rem] items-center justify-center p-3">
        {src ? (
          <img src={src} alt={label} className={imgClassName} />
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex flex-col items-center gap-1 text-slate-400 hover:text-primary"
          >
            <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 28 }} />
            <span className="text-xs font-medium">{emptyLabel}</span>
          </button>
        )}
      </div>
      {pending ? (
        <p className="border-t border-amber-200/80 bg-amber-50 px-3 py-1.5 text-[10px] font-medium text-amber-700 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-300">
          New crop ready — save to apply
        </p>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-primary/[0.04] p-4 sm:p-5 dark:border-slate-700/80 dark:from-slate-800/80 dark:via-slate-800/60 dark:to-primary/10">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center xl:gap-8">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt="Clinic logo"
              className="h-24 w-24 shrink-0 rounded-2xl border border-slate-200/80 bg-white object-contain p-2 shadow-sm dark:border-slate-600 xl:h-28 xl:w-28"
            />
          ) : (
            <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primaryLight shadow-lg shadow-primary/20 xl:h-28 xl:w-28">
              <BusinessIcon sx={{ fontSize: 40 }} className="text-white" />
            </div>
          )}
          <div className="min-w-0 flex-1 xl:flex xl:items-end xl:justify-between xl:gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-primary">
                Clinic profile
              </p>
              <p className="mt-1 text-2xl font-bold capitalize tracking-tight text-slate-900 dark:text-white xl:text-3xl">
                {displayName}
              </p>
              {clinic?.clinicId ? (
                <p className="mt-1.5 inline-flex items-center rounded-lg bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-600 dark:bg-slate-700/60 dark:text-slate-300">
                  ID {clinic.clinicId}
                </p>
              ) : null}
              {(clinic?.address || addressFallback) && (
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 xl:max-w-xl">
                  {clinic?.address || addressFallback}
                </p>
              )}
            </div>
            <div className="mt-4 hidden xl:mt-0 xl:grid xl:grid-cols-2 xl:gap-3">
              <MetricTile label="Staff limit" value={clinic?.employeeCount ?? "—"} />
              <MetricTile
                label="Appointment app"
                value={<BoolBadge value={clinic?.appointmentApp} />}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="xl:grid xl:grid-cols-12 xl:items-start xl:gap-4">
        <div className="space-y-4 xl:col-span-7">
          <PanelCard className="p-4 sm:p-5">
            <SectionHeader icon={BusinessIcon} title="General information" />
            <div className="xl:grid xl:grid-cols-2 xl:gap-4">
              <div>
                <SubsectionTitle>General</SubsectionTitle>
                <DetailsBlock>
                  <DetailRow label="Clinic name" value={displayName} />
                  <DetailRow label="Clinic ID" value={clinic?.clinicId} />
                  <DetailRow label="Bill / print title" value={clinic?.title} />
                  <DetailRow label="Owner name" value={clinic?.ownerName} />
                  <DetailRow label="Address" value={clinic?.address || addressFallback} />
                  <DetailRow label="Telephone" value={clinic?.tpNo || phoneFallback} />
                </DetailsBlock>
              </div>
              <div>
                <SubsectionTitle>Registration &amp; hours</SubsectionTitle>
                <DetailsBlock>
                  <DetailRow label="Registration no." value={clinic?.registerNo} />
                  <DetailRow label="Logo on documents" value={<BoolBadge value={clinic?.logo} />} />
                  <DetailRow label="Reg. no. method" value={clinic?.regNoMethod} />
                  <DetailRow label="Reg. no. format" value={clinic?.regNoFormat} />
                  <DetailRow label="Starting number" value={clinic?.reqNoStart} />
                  <DetailRow label="Start time" value={clinic?.startTime} />
                  <DetailRow label="End time" value={clinic?.endTime} />
                  <DetailRow label="Slot duration" value={clinic?.timeDuration} />
                </DetailsBlock>
              </div>
            </div>
          </PanelCard>

          <PanelCard className="p-4 sm:p-5">
            <SectionHeader
              icon={PaletteOutlinedIcon}
              title="Branding images"
              subtitle="Logo & logo small use fixed crop · print image uses free crop"
            />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {renderEditableCard({
                label: "Logo",
                sizeLabel: `${LOGO_SIZE.width}×${LOGO_SIZE.height}`,
                src: logoSrc,
                pending: Boolean(pendingLogo),
                inputRef: logoInputRef,
                target: "logo",
                emptyLabel: "Add logo",
                imgClassName: "max-h-28 w-full object-contain",
              })}
              {renderEditableCard({
                label: "Logo small",
                sizeLabel: `${LOGO_SMALL_SIZE.width}×${LOGO_SMALL_SIZE.height}`,
                src: logoSmallSrc,
                pending: Boolean(pendingLogoSmall),
                inputRef: logoSmallInputRef,
                target: "logoSmall",
                emptyLabel: "Add logo small",
                imgClassName: "h-14 w-14 object-contain",
              })}
              {renderEditableCard({
                label: "Print image",
                sizeLabel: "free crop",
                src: printSrc,
                pending: Boolean(pendingPrint),
                inputRef: printInputRef,
                target: "print",
                emptyLabel: "Add print image",
                imgClassName: "max-h-28 w-full object-contain",
              })}
            </div>

            {otherBranding.length > 0 ? (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {otherBranding.map(({ label, src }) => (
                  <div
                    key={label}
                    className="overflow-hidden rounded-xl border border-slate-200/80 bg-slate-50 dark:border-slate-600/80 dark:bg-slate-900/40"
                  >
                    <p className="border-b border-slate-100 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:border-slate-700">
                      {label}
                    </p>
                    <img src={src} alt={label} className="max-h-28 w-full object-contain p-3" />
                  </div>
                ))}
              </div>
            ) : null}

            {hasPending ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={saveBranding}
                  className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/95 disabled:opacity-60"
                >
                  {saving ? "Saving…" : "Save branding"}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={discardPending}
                  className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Discard
                </button>
              </div>
            ) : null}
          </PanelCard>
        </div>

        <div className="space-y-4 xl:col-span-5">
          <PanelCard className="p-4 sm:p-5">
            <SectionHeader
              icon={SettingsOutlinedIcon}
              title="Features & limits"
              subtitle="Enabled modules and capacity"
            />
            <div className="grid grid-cols-2 gap-3">
              <MetricTile
                label="Advanced chart"
                value={<BoolBadge value={clinic?.advancedChart} />}
              />
              <MetricTile
                label="Appointment app"
                value={<BoolBadge value={clinic?.appointmentApp} />}
              />
              <MetricTile
                label="Dental labs"
                value={<BoolBadge value={clinic?.featureList?.labs} />}
              />
              <MetricTile
                label="Room allocation"
                value={<BoolBadge value={clinic?.featureList?.roomAllocation} />}
              />
              <MetricTile
                label="Print template 2"
                value={<BoolBadge value={clinic?.featureList?.printTemplate_2} />}
              />
              <MetricTile
                label="Doctor transfer"
                value={<BoolBadge value={clinic?.featureList?.doctorTransfer} />}
              />
              <MetricTile label="App doctor limit" value={clinic?.appDocLimit ?? "—"} />
              <MetricTile label="Staff limit" value={clinic?.employeeCount ?? "—"} />
            </div>
            <div className="mt-4">
              <DetailsBlock>
                <DetailRow
                  label="Queue screen videos"
                  value={
                    queueCount > 0 ? `${queueCount} video${queueCount === 1 ? "" : "s"}` : "—"
                  }
                />
              </DetailsBlock>
              {queueCount > 0 ? (
                <ul className="mt-3 space-y-2">
                  {clinic!.queueVideos!.map((v, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2 text-sm dark:border-slate-700/60 dark:bg-slate-900/30"
                    >
                      <span className="font-medium text-slate-800 dark:text-slate-200">
                        {v.originalName || `Video ${i + 1}`}
                      </span>
                      {v.size != null ? (
                        <span className="text-xs text-slate-500">{formatBytes(v.size)}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </PanelCard>

          <PanelCard className="p-4 sm:p-5">
            <SectionHeader icon={ScheduleOutlinedIcon} title="Record" />
            <DetailsBlock>
              <DetailRow label="Created" value={formatDate(clinic?.createdAt)} />
              <DetailRow label="Last updated" value={formatDate(clinic?.updatedAt)} />
            </DetailsBlock>
          </PanelCard>
        </div>
      </div>

      {cropSrc && cropTarget ? (
        <ImageCropModal
          src={cropSrc}
          title={cropTitle}
          freeCrop={cropTarget === "print"}
          targetWidth={
            cropTarget === "logo"
              ? LOGO_SIZE.width
              : cropTarget === "logoSmall"
                ? LOGO_SMALL_SIZE.width
                : undefined
          }
          targetHeight={
            cropTarget === "logo"
              ? LOGO_SIZE.height
              : cropTarget === "logoSmall"
                ? LOGO_SMALL_SIZE.height
                : undefined
          }
          onCancel={closeCrop}
          onConfirm={applyCrop}
        />
      ) : null}
    </div>
  );
}
